/* global globalThis */
// Only short review crops and their actual foreground masks belong in this cache.
const CACHE_PREFIX = 'ting-motion-media-';
const CACHE_VERSION = 1;
const CACHE_NAME = `${CACHE_PREFIX}v${CACHE_VERSION}`;
const MIB = 1024 * 1024;

export function createMotionMediaBuffer(options = {}) {
  const fetchMedia = options.fetch || globalThis.fetch?.bind(globalThis);
  const storage = options.cacheStorage === undefined ? globalThis.caches : options.cacheStorage;
  const urls = options.URL || globalThis.URL;
  const ResponseType = options.Response || globalThis.Response;
  const origin = options.origin || globalThis.location?.origin || 'https://motion-review.invalid';
  const concurrency = Math.max(1, Math.min(3, options.concurrency || 3));
  const maxTasks = Math.max(1, Math.min(50, options.maxTasks || 50));
  const maxBytes = options.maxBytes || 150 * MIB;
  const maxClipBytes = options.maxClipBytes || 16 * MIB;
  const maxBlobBytes = options.maxBlobBytes || 48 * MIB;
  const warmCount = Math.max(1, options.warmCount || 4); // Current plus the next three.
  const timeoutMs = options.timeoutMs || 15000;
  const cacheInitTimeoutMs = options.cacheInitTimeoutMs || 1500;
  const entries = new Map();
  const choices = new Map();
  const disk = new Map();
  const idleWaiters = [];
  let tasks = [];
  let previousTask = null;
  let mediaDescriptors = [];
  let nearbyKeys = new Set();
  let taskMedia = new Map();
  let includeMasks = false;
  let preloadEnabled = options.preloadEnabled !== false;
  let disposed = false;
  let active = 0;
  let cache = null;
  let initialization = null;
  let diskWrites = Promise.resolve();
  let epoch = 0;

  function descriptor(task, kind) {
    const raw = kind === 'raw' ? task.videoUrl : task.maskVideoUrl;
    if (!task.id || !raw) return null;
    let source;
    try { const url = new URL(raw, origin); source = `${url.origin}${url.pathname}`; } catch { return null; }
    const key = `${task.id}|${kind}|${source}`;
    const cacheKey = `${origin}/__motion_media__/v1/${encodeURIComponent(task.id)}/${kind}?source=${encodeURIComponent(source)}`;
    return { key, cacheKey, taskId: task.id, kind, url: raw };
  }

  function desired() {
    return mediaDescriptors;
  }

  function nearKeys() {
    return nearbyKeys;
  }

  function blobBytes() { return [...entries.values()].reduce((sum, entry) => sum + (entry.blobUrl ? entry.size : 0), 0); }
  function cacheBytes() { return [...disk.values()].reduce((sum, item) => sum + item.size, 0); }
  function hasMedia(entry) { return Boolean(entry && (entry.blobUrl || disk.has(entry.cacheKey))); }

  function getStatus() {
    const media = desired();
    const readyCount = tasks.filter(task => {
      const { raw, mask } = taskMedia.get(task.id) || {};
      return raw && hasMedia(entries.get(raw.key)) && (!mask || hasMedia(entries.get(mask.key)));
    }).length;
    const previousMedia = previousTask && taskMedia.get(previousTask.id);
    return {
      metadataCount: tasks.length,
      preloadEnabled,
      previousCount: previousTask ? 1 : 0,
      previousReady: Boolean(previousMedia?.raw && entries.get(previousMedia.raw.key)?.blobUrl &&
        (!previousMedia.mask || entries.get(previousMedia.mask.key)?.blobUrl)),
      readyCount, readyTasks: readyCount,
      warmCount: tasks.filter(task => {
        const { raw, mask } = taskMedia.get(task.id) || {};
        return raw && entries.get(raw.key)?.blobUrl && (!mask || entries.get(mask.key)?.blobUrl);
      }).length,
      mediaCount: media.length,
      mediaReadyCount: media.filter(item => hasMedia(entries.get(item.key))).length,
      loadingCount: active,
      failedCount: [...entries.values()].filter(entry => entry.state === 'failed' || entry.state === 'oversize').length,
      cacheBytes: cacheBytes(), blobBytes: blobBytes(),
    };
  }

  function notify() {
    if (!disposed) { try { options.onWarm?.(getStatus()); } catch {} }
  }

  function revoke(entry) {
    if (entry.blobUrl) {
      urls.revokeObjectURL(entry.blobUrl);
      for (const choice of choices.values()) {
        for (const kind of ['raw', 'mask']) if (choice[kind]?.url === entry.blobUrl) delete choice[kind];
      }
    }
    entry.blobUrl = null;
  }

  function warm(entry, blob) {
    if (disposed || entries.get(entry.key) !== entry || !nearKeys().has(entry.key) || entry.blobUrl) return;
    if (blob.size > maxBlobBytes || blobBytes() + blob.size > maxBlobBytes || !urls?.createObjectURL) return;
    entry.size = blob.size;
    entry.blobUrl = urls.createObjectURL(blob);
  }

  async function initialize() {
    if (initialization) return initialization;
    const generation = epoch;
    let expired = false;
    let timer;
    const opening = (async () => {
      try {
        if (!storage || !ResponseType) return;
        for (const name of await storage.keys()) {
          const version = Number(name.match(/^ting-motion-media-v(\d+)$/)?.[1] ?? NaN);
          if (Number.isInteger(version) && version < CACHE_VERSION) await storage.delete(name);
        }
        const opened = await storage.open(CACHE_NAME);
        const found = new Map();
        for (const request of await opened.keys()) {
          const response = await opened.match(request);
          const size = Number(response?.headers.get('x-motion-bytes'));
          const touched = Number(response?.headers.get('x-motion-touched')) || 0;
          const key = typeof request === 'string' ? request : request.url;
          if (!Number.isSafeInteger(size) || size <= 0 || size > maxClipBytes) { await opened.delete(request); continue; }
          found.set(key, { size, touched });
        }
        if (disposed || epoch !== generation || expired) return;
        cache = opened;
        for (const [key, value] of found) disk.set(key, value);
        await trimDisk(0);
      } catch { if (!expired && epoch === generation) { cache = null; disk.clear(); } }
    })();
    // WebKit storage can stall without rejecting. Standby downloads still work
    // in bounded memory when that happens; a late index must not replace them.
    initialization = Promise.race([opening, new Promise(resolve => {
      timer = setTimeout(() => { expired = true; cache = null; disk.clear(); resolve(); }, cacheInitTimeoutMs);
    })]).finally(() => clearTimeout(timer));
    return initialization;
  }

  async function trimDisk(extra, preserveKey) {
    if (!cache) return;
    const rank = new Map(desired().map((entry, index) => [entry.cacheKey, index]));
    const candidates = [...disk.entries()].filter(([key]) => key !== preserveKey)
      .sort(([a, av], [b, bv]) => {
        const ar = rank.has(a) ? rank.get(a) : Infinity;
        const br = rank.has(b) ? rank.get(b) : Infinity;
        return ar === br ? av.touched - bv.touched : br - ar;
      });
    for (const [key] of candidates) {
      if (cacheBytes() + extra <= maxBytes) break;
      await cache.delete(key);
      disk.delete(key);
      for (const entry of entries.values()) if (entry.cacheKey === key && !entry.blobUrl) entry.state = 'evicted';
    }
  }

  async function storeBlob(entry, blob, generation) {
    if (!cache || !ResponseType || blob.size > maxBytes) return false;
    const operation = diskWrites.then(async () => {
      if (disposed || epoch !== generation || entries.get(entry.key) !== entry) return false;
      const oldSize = disk.get(entry.cacheKey)?.size || 0;
      await trimDisk(Math.max(0, blob.size - oldSize), entry.cacheKey);
      try {
        await cache.put(entry.cacheKey, new ResponseType(blob, { headers: {
          'Content-Type': blob.type || 'video/mp4', 'Content-Length': String(blob.size),
          'x-motion-bytes': String(blob.size), 'x-motion-touched': String(Date.now()),
        } }));
        disk.set(entry.cacheKey, { size: blob.size, touched: Date.now() });
        return true;
      } catch { return false; } // Quota/private mode/CORS failures preserve normal playback.
    });
    diskWrites = operation.catch(() => {});
    return operation;
  }

  async function boundedBlob(response) {
    const declared = Number(response.headers.get('Content-Length'));
    if (declared > maxClipBytes) {
      await response.body?.cancel?.();
      throw new RangeError('Motion clip exceeds media cache limit');
    }
    if (!response.body?.getReader) {
      if (!declared || declared > maxClipBytes) throw new RangeError('Unknown motion clip size');
      const blob = await response.blob();
      if (blob.size > maxClipBytes) throw new RangeError('Motion clip exceeds media cache limit');
      return blob;
    }
    const reader = response.body.getReader();
    const chunks = [];
    let size = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > maxClipBytes) {
          await reader.cancel();
          throw new RangeError('Motion clip exceeds media cache limit');
        }
        chunks.push(value);
      }
    } finally { reader.releaseLock?.(); }
    if (!size) throw new Error('Motion clip is empty');
    return new Blob(chunks, { type: response.headers.get('Content-Type') || 'video/mp4' });
  }

  async function load(entry) {
    const generation = epoch;
    entry.state = 'loading';
    try {
      const cached = cache && await cache.match(entry.cacheKey);
      if (cached) {
        entry.size = Number(cached.headers.get('x-motion-bytes')) || 0;
        if (nearKeys().has(entry.key) && blobBytes() + entry.size <= maxBlobBytes) warm(entry, await cached.blob());
        entry.state = 'ready';
        return;
      }
      disk.delete(entry.cacheKey);
      if (!fetchMedia) throw new Error('Media fetch unavailable');
      entry.controller = new AbortController();
      const timer = setTimeout(() => entry.controller?.abort(), timeoutMs);
      let blob;
      try {
        const response = await fetchMedia(entry.url, { mode: 'cors', credentials: 'omit', signal: entry.controller.signal });
        if (!response.ok || response.status === 206 || response.type === 'opaque') throw new Error('Motion media could not be prefetched');
        blob = await boundedBlob(response);
      } finally { clearTimeout(timer); }
      if (disposed || epoch !== generation || entries.get(entry.key) !== entry) return;
      entry.size = blob.size;
      warm(entry, blob);
      notify(); // First-frame preparation can start before a slow disk-cache write.
      const stored = await storeBlob(entry, blob, generation);
      entry.state = stored || entry.blobUrl ? 'ready' : 'uncached';
    } catch (error) {
      entry.state = error instanceof RangeError ? 'oversize' : 'failed';
    } finally { entry.controller = null; }
  }

  function pendingEntries() {
    const near = nearKeys();
    const canWarm = entry => urls?.createObjectURL && (entry.size || 0) <= maxBlobBytes && blobBytes() + (entry.size || 0) <= maxBlobBytes;
    return desired().map(item => entries.get(item.key)).filter(entry => entry && (
      entry.state === 'queued' || (near.has(entry.key) && !entry.blobUrl && canWarm(entry) &&
        (entry.state === 'evicted' || entry.state === 'uncached' ||
          entry.state === 'ready'))
    ) && (cache || near.has(entry.key)));
  }

  function pump() {
    if (disposed) return;
    if (!preloadEnabled) {
      idleWaiters.splice(0).forEach(resolve => resolve(getStatus()));
      return;
    }
    const onSettled = () => { active -= 1; notify(); pump(); };
    let next;
    while (active < concurrency && (next = pendingEntries()[0])) {
      active += 1;
      load(next).finally(onSettled);
    }
    if (!active && !pendingEntries().length) idleWaiters.splice(0).forEach(resolve => resolve(getStatus()));
  }

  function reconcileTasks() {
    // The latest answer is separate from the upcoming queue. Give its disk
    // hydration priority immediately after the current clip, not after 50.
    const allTasks = [...tasks];
    if (previousTask && !allTasks.some(task => task.id === previousTask.id)) allTasks.splice(Math.min(warmCount, allTasks.length), 0, previousTask);
    taskMedia = new Map(allTasks.map(task => [task.id, { raw: descriptor(task, 'raw'), mask: includeMasks ? descriptor(task, 'mask') : null }]));
    mediaDescriptors = allTasks.flatMap(task => Object.values(taskMedia.get(task.id)).filter(Boolean));
    const warmTasks = [...tasks.slice(0, warmCount), ...(previousTask ? [previousTask] : [])];
    nearbyKeys = new Set(warmTasks.flatMap(task => Object.values(taskMedia.get(task.id)).filter(Boolean).map(item => item.key)));
    const media = desired();
    const keep = new Set(media.map(item => item.key));
    const near = nearKeys();
    for (const [key, entry] of entries) {
      if (!keep.has(key)) { entry.controller?.abort(); revoke(entry); entries.delete(key); }
      else if (!near.has(key) && ![...choices.values()].some(choice => choice.raw?.key === key || choice.mask?.key === key)) revoke(entry);
    }
    for (const id of choices.keys()) if (!taskMedia.has(id)) choices.delete(id);
    for (const item of media) {
      const old = entries.get(item.key);
      if (!old) entries.set(item.key, { ...item, state: 'queued', size: 0, blobUrl: null });
      else if (old.url !== item.url) { old.url = item.url; if (old.state === 'failed') old.state = 'queued'; }
    }
    if (preloadEnabled) initialize().then(() => { if (!disposed) pump(); });
    return getStatus();
  }

  function setPreloadEnabled(enabled) {
    if (disposed) return getStatus();
    preloadEnabled = enabled === true;
    // The screen chooses when to start background indexing and downloads,
    // independently of whether the browser has reported its first frame.
    if (preloadEnabled) initialize().then(() => { if (!disposed) pump(); });
    else if (!active) idleWaiters.splice(0).forEach(resolve => resolve(getStatus()));
    notify();
    return getStatus();
  }

  function syncTasks(nextTasks, settings = {}) {
    if (disposed) return getStatus();
    const seen = new Set();
    tasks = (nextTasks || []).filter(task => task?.id && !seen.has(task.id) && seen.add(task.id)).slice(0, maxTasks);
    includeMasks = settings.includeMasks === true;
    if (Object.prototype.hasOwnProperty.call(settings, 'previousTasks')) {
      previousTask = (settings.previousTasks || []).find(task => task?.id && task.videoUrl) || null;
    }
    return reconcileTasks();
  }

  function retainPrevious(task) {
    if (disposed) return getStatus();
    previousTask = task?.id && task.videoUrl ? task : null;
    // The answer is leaving the screen. Undo should select its retained blob
    // even if the first presentation began on a direct network URL.
    if (previousTask) choices.delete(previousTask.id);
    return reconcileTasks();
  }

  function readyTask(task) {
    if (!task || disposed) return task;
    let choice = choices.get(task.id);
    if (!choice) { choice = {}; choices.set(task.id, choice); }
    const pick = kind => {
      const item = descriptor(task, kind);
      if (!item) return null;
      if (choice[kind]?.key !== item.key) choice[kind] = { key: item.key, url: entries.get(item.key)?.blobUrl || item.url };
      return choice[kind].url;
    };
    // Once displayed, a task keeps its chosen src even if its background fetch
    // completes later. This prevents replay/remount during rapid review.
    return { ...task, videoUrl: pick('raw'), maskVideoUrl: includeMasks ? pick('mask') : task.maskVideoUrl };
  }

  async function clear({ disk: clearDisk = true } = {}) {
    epoch += 1;
    for (const entry of entries.values()) { entry.controller?.abort(); revoke(entry); }
    entries.clear(); choices.clear(); tasks = []; previousTask = null; disk.clear(); mediaDescriptors = []; nearbyKeys.clear(); taskMedia.clear();
    await diskWrites;
    disk.clear();
    if (clearDisk && storage) { try { await storage.delete(CACHE_NAME); } catch {} }
    cache = null; initialization = null;
    idleWaiters.splice(0).forEach(resolve => resolve(getStatus()));
  }

  return {
    syncTasks, retainPrevious, readyTask, getStatus, setPreloadEnabled,
    // Only mount warm decoders once bytes are available. If CORS/cache limits
    // prevent prefetching, let the upcoming video use normal browser loading.
    decoderTask: task => {
      const item = descriptor(task, 'raw');
      const entry = item && entries.get(item.key);
      return entry && (entry.blobUrl || ['failed', 'oversize', 'uncached'].includes(entry.state)) ? readyTask(task) : null;
    },
    prepare: async task => readyTask(task),
    releaseTask: id => choices.delete(id), // Explicit playback failure/URL refresh only.
    forgetPresentation: id => choices.delete(id),
    idle: async () => { if (!preloadEnabled || disposed) return getStatus(); await initialize(); if (!preloadEnabled || (!active && !pendingEntries().length)) return getStatus(); return new Promise(resolve => idleWaiters.push(resolve)); },
    clear,
    dispose: () => { disposed = true; return clear({ disk: false }); },
  };
}

export default createMotionMediaBuffer;
